import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import {
  hydrateXpConfig,
  type XpConfigRow,
  type XpActionRow,
  type XpRateRow,
  type XpTitleRow,
} from '../utils/levelCalculation';
import { hydrateSocialXpConfig } from '../constants/socialXP';
import { hydrateBreathworkRanks } from '../constants/breathwork';
import { hydrateIntellectualXp } from '../constants/intellectualSpheres';
import { hydrateEcologicalXpLadders } from '../utils/ecologicalXP';

/**
 * XP config — loads the console-managed XP economy tables (migrations
 * 0191/0192) and hydrates the XP math + title ladders on boot:
 *   - utils/levelCalculation.ts (POINTS, curve, streak ladder, ranks, tiers)
 *   - constants/socialXP.ts (action values, titles, multipliers, level size —
 *     the SAME rows award_social_xp() reads server-side since Phase B)
 *   - constants/breathwork.ts (rank names + XP thresholds; the highest row
 *     is the rank cap)
 *
 * Fail-open by design: if the tables don't exist yet, the fetch errors, or
 * the user is offline, the hardcoded defaults (identical to the seeds) stay
 * in force — behavior is unchanged.
 */
interface XpConfigState {
  loaded: boolean;
  initialized: boolean;
  initialize: () => Promise<void>;
}

export const useXpConfigStore = create<XpConfigState>((set, get) => ({
  loaded: false,
  initialized: false,

  initialize: async () => {
    if (get().initialized) return;
    set({ initialized: true });
    try {
      const [config, actions, rates, titles] = await Promise.all([
        supabase.from('xp_config').select('*').eq('id', 1).maybeSingle(),
        supabase.from('xp_actions').select('key, base_xp, active, category').eq('active', true),
        supabase.from('xp_rates').select('kind, key, threshold, multiplier, active').eq('active', true),
        // ⚠️⚠️ `*`, NOT A COLUMN LIST. Naming columns here couples the app's
        // deploy order to the migration's: selecting a column the DB does not
        // have yet fails the WHOLE query, `titleRows` arrives empty, and every
        // ladder silently drops to its hardcoded default — so a per-domain
        // ladder would show generic names until the migration landed. With `*`
        // a not-yet-added column is simply absent and the rest still hydrates.
        // The table is config-sized (tens of rows), so there is nothing to save
        // by listing them.
        supabase.from('xp_titles').select('*').eq('active', true),
      ]);
      if (config.error && actions.error && rates.error) return; // pre-migration / offline — keep defaults

      const configRow = (config.data as XpConfigRow & { social_xp_per_level?: number } | null) ?? null;
      const actionRows = (actions.data ?? []) as XpActionRow[];
      const rateRows = (rates.data ?? []) as XpRateRow[];
      const titleRows = (titles.data ?? []) as XpTitleRow[];

      hydrateXpConfig(configRow, actionRows, rateRows, titleRows);
      hydrateSocialXpConfig(
        actionRows,
        titleRows.filter((t) => t.kind === 'social_title'),
        rateRows,
        configRow?.social_xp_per_level ?? null,
      );
      hydrateBreathworkRanks(titleRows.filter((t) => t.kind === 'breathwork_rank'));
      hydrateIntellectualXp(actionRows, rateRows);
      hydrateEcologicalXpLadders(rateRows);

      set({ loaded: true });
    } catch (err) {
      console.error('XP config load failed (defaults stay in force):', err);
    }
  },
}));
